import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Build as BuildIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Event as EventIcon,
  Gavel as GavelIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  Payments as PaymentsIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Repeat as RepeatIcon,
  Restore as RestoreIcon,
  Snooze as SnoozeIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { DashboardAlert, DashboardAlertEventType, DashboardAlertIconKey } from '../services/alertService';

// ---- visual tokens --------------------------------------------------------

const ALERT_ICON_MAP: Record<DashboardAlertIconKey, React.ReactNode> = {
  return: <HistoryIcon sx={{ fontSize: 18 }} />,
  handover: <EventIcon sx={{ fontSize: 18 }} />,
  payment: <PaymentsIcon sx={{ fontSize: 18 }} />,
  document: <DescriptionIcon sx={{ fontSize: 18 }} />,
  lease: <PaymentsIcon sx={{ fontSize: 18 }} />,
  license: <CreditCardIcon sx={{ fontSize: 18 }} />,
  contract: <GavelIcon sx={{ fontSize: 18 }} />,
  wrench: <BuildIcon sx={{ fontSize: 18 }} />,
  recurring: <RepeatIcon sx={{ fontSize: 18 }} />,
  drawer: <WalletIcon sx={{ fontSize: 18 }} />,
};

const alertTone: Record<DashboardAlert['severity'], { bg: string; color: string; border: string }> = {
  error: { bg: '#FEF2F2', color: '#DC2626', border: '#FCA5A5' },
  warning: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
  info: { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
};

const PRIORITY_DOT: Record<DashboardAlert['priority'], string | null> = {
  critical: '#DC2626',
  high: '#D97706',
  normal: null,
  low: null,
};

// Stable labels for known group keys. Unknown keys fall back to the first
// member's title so an alert builder can add new groups without code changes.
const GROUP_LABELS: Record<string, { labelKey: string; defaultLabel: string }> = {
  'vehicle:document_expiry': {
    labelKey: 'alerts.groups.documentExpiry',
    defaultLabel: 'documents need attention',
  },
  'vehicle:vehicle_maintenance_due': {
    labelKey: 'alerts.groups.maintenanceDue',
    defaultLabel: 'vehicles need maintenance',
  },
  'booking:client_license_expiring': {
    labelKey: 'alerts.groups.licenseExpiring',
    defaultLabel: 'customer licenses expiring',
  },
  'expense:monthly_fixed_costs_due': {
    labelKey: 'alerts.groups.monthlyFixedCosts',
    defaultLabel: 'monthly payments due',
  },
};

// Choose the most informative audit event to summarise in the row caption.
// Order matters: an unresolved action (snoozed/assigned) is more interesting
// than the creation timestamp, but a dismissed event trumps everything.
function lastSignificantEvent(alert: DashboardAlert) {
  const events = alert.events || [];
  if (events.length === 0) return null;
  const dismissed = [...events].reverse().find((e) => e.type === 'dismissed' || e.type === 'undismissed');
  if (dismissed) return dismissed;
  const assigned = [...events].reverse().find((e) => e.type === 'assigned' || e.type === 'unassigned');
  if (assigned) return assigned;
  const snoozed = [...events].reverse().find((e) => e.type === 'snoozed' || e.type === 'unsnoozed');
  if (snoozed) return snoozed;
  return events[events.length - 1];
}

function formatRelative(at: string, t: (key: string, opts?: any) => string) {
  const date = new Date(at);
  const diffMs = Date.now() - date.getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return '';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('notifications.justNow', { defaultValue: 'Just now' });
  if (minutes < 60) return t('notifications.minutesAgo', { defaultValue: '{{count}}m ago', count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { defaultValue: '{{count}}h ago', count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.daysAgo', { defaultValue: '{{count}}d ago', count: days });
}

// English labels the backend bakes into `subtitle`/`meta`. We translate them
// on the client because the persisted alert text is generated server-side in
// English. Numbers and proper names (customer, vehicle, plate) stay untouched.
const PAYMENT_STATUS_FALLBACK: Record<string, string> = {
  unpaid: 'Unpaid',
  partial: 'Partial',
  pending: 'Pending',
  paid: 'Paid',
  refunded: 'Refunded',
};

const ALERT_EVENT_FALLBACK: Record<DashboardAlertEventType, string> = {
  created: 'Created',
  updated: 'Updated',
  dismissed: 'Dismissed',
  undismissed: 'Restored',
  snoozed: 'Snoozed',
  unsnoozed: 'Unsnoozed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const translatePaymentStatus = (status: string, t: Translate, fallback?: string) => {
  const key = status.toLowerCase();
  return t(`alerts.paymentStatus.${key}`, {
    defaultValue: PAYMENT_STATUS_FALLBACK[key] || fallback || status,
  });
};

const translateAlertEventType = (eventType: DashboardAlertEventType, t: Translate) =>
  t(`alerts.events.${eventType}`, {
    defaultValue: ALERT_EVENT_FALLBACK[eventType] || eventType,
  });

const translateBackendAlertPhrase = (text: string, t: Translate) => {
  if (!text) return text;
  if (/^today$/i.test(text.trim())) {
    return t('alerts.meta.today', { defaultValue: 'Today' });
  }

  return text
    .replace(/\b(\d+)\s+days?\s+unpaid\b/gi, (_match, count) =>
      t('alerts.phrases.daysUnpaid', {
        count: Number(count),
        defaultValue: `${count} days unpaid`,
      }))
    .replace(/\b(\d+)\s*d\s+late\b/gi, (_match, count) =>
      t('alerts.meta.daysLate', {
        count: Number(count),
        defaultValue: `${count}d late`,
      }))
    .replace(/\b(\d+)\s*d\s+left\b/gi, (_match, count) =>
      t('alerts.meta.daysLeft', {
        count: Number(count),
        defaultValue: `${count}d left`,
      }))
    .replace(/\b(Unpaid|Partial|Pending|Paid|Refunded)\b/gi, (match) =>
      translatePaymentStatus(match, t, match));
};

// Rebuild the right-hand "meta" chip from the structured snapshot day-count so
// it reads in the active language instead of the backend's "5d late"/"Today".
// Returns null when there's no usable day-count (caller keeps the backend text).
const dueMetaFromDays = (value: unknown, t: Translate): string | null => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return null;
  const days = Number(value);
  if (days < 0) return t('alerts.meta.daysLate', { count: Math.abs(days), defaultValue: '{{count}}d late' });
  if (days === 0) return t('alerts.meta.today', { defaultValue: 'Today' });
  return t('alerts.meta.daysLeft', { count: days, defaultValue: '{{count}}d left' });
};

const getAlertText = (
  alert: DashboardAlert,
  t: Translate,
) => {
  const snapshot = alert.snapshot || {};
  const fallbackTitle = alert.title;
  const fallbackSubtitle = alert.subtitle;
  const fallbackMeta = alert.meta;
  const translatedFallbackSubtitle = translateBackendAlertPhrase(fallbackSubtitle, t);
  const translatedFallbackMeta = translateBackendAlertPhrase(fallbackMeta, t);
  const subKey = alert.source?.subKey || '';
  const bookingNumber = fallbackTitle.match(/([A-Z]{1,8}-?\d[\w-]*|\d[\w-]*)$/)?.[1] || '';
  const documentLabel = alert.type === 'document_expiry'
    ? t(`alerts.documentLabels.${subKey}`, { defaultValue: fallbackTitle.replace(/\s+(expired|expiring)$/i, '') })
    : fallbackTitle.replace(/\s+(expired|expiring|due|missed)$/i, '');

  const freqRaw = String(snapshot.frequency || 'monthly');
  const common = {
    title: fallbackTitle,
    subtitle: translatedFallbackSubtitle,
    meta: translatedFallbackMeta,
    booking: bookingNumber,
    label: documentLabel,
    reason: subKey,
    frequency: t(`alerts.frequency.${freqRaw}`, { defaultValue: freqRaw }),
    days: Number(snapshot.daysUntilExpiry ?? snapshot.daysRemaining ?? snapshot.daysUntilDue ?? snapshot.daysLate ?? snapshot.daysOverdue ?? 0),
  };

  switch (alert.type) {
    case 'overdue_return':
      return {
        title: t('alerts.types.overdue_return.title', { ...common, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(-Math.abs(Number(snapshot.daysLate ?? 0)), t) || translatedFallbackMeta,
      };
    case 'missed_handover':
      return {
        title: t('alerts.types.missed_handover.title', { ...common, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(-Math.abs(Number(snapshot.daysLate ?? 0)), t) || translatedFallbackMeta,
      };
    case 'unpaid_balance':
      return {
        title: t('alerts.types.unpaid_balance.title', { ...common, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: translatedFallbackMeta,
      };
    case 'post_return_balance':
      return {
        title: t('alerts.types.post_return_balance.title', { ...common, defaultValue: fallbackTitle }),
        subtitle: translateBackendAlertPhrase(
          fallbackSubtitle.replace(/returned\s+(\d+)d\s+ago\s*$/i, (_match, d) =>
            t('alerts.types.post_return_balance.returnedAgo', { count: Number(d), defaultValue: `returned ${d}d ago` })),
          t,
        ),
        meta: translatedFallbackMeta,
      };
    case 'contract_unsigned':
      return {
        title: t('alerts.types.contract_unsigned.title', { ...common, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(snapshot.daysUntilStart, t) || translatedFallbackMeta,
      };
    case 'client_license_expiring':
      return {
        title: common.days < 0
          ? t('alerts.types.client_license_expiring.expiredTitle', { defaultValue: fallbackTitle })
          : t('alerts.types.client_license_expiring.expiringTitle', { defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(snapshot.daysUntilExpiry, t) || translatedFallbackMeta,
      };
    case 'document_expiry':
      return {
        title: common.days < 0
          ? t('alerts.types.document_expiry.expiredTitle', { label: common.label, defaultValue: fallbackTitle })
          : t('alerts.types.document_expiry.expiringTitle', { label: common.label, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(snapshot.daysRemaining, t) || translatedFallbackMeta,
      };
    case 'vehicle_maintenance_due':
      return {
        title: t(`alerts.types.vehicle_maintenance_due.${subKey || 'default'}`, { defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: snapshot.reason === 'km'
          ? t('alerts.meta.kmLeft', { count: Math.max(0, Number(snapshot.kmRemaining ?? 0)), defaultValue: '{{count}} km left' })
          : (dueMetaFromDays(snapshot.daysRemaining, t) || translatedFallbackMeta),
      };
    case 'lease_payment_missed':
      return {
        title: t('alerts.types.lease_payment_missed.title', { defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(-Math.abs(Number(snapshot.daysOverdue ?? 0)), t) || translatedFallbackMeta,
      };
    case 'monthly_fixed_costs_due':
      return {
        title: common.days < 0
          ? t('alerts.types.monthly_fixed_costs_due.overdueTitle', { defaultValue: fallbackTitle })
          : t('alerts.types.monthly_fixed_costs_due.upcomingTitle', { frequency: common.frequency, defaultValue: fallbackTitle }),
        subtitle: translatedFallbackSubtitle,
        meta: dueMetaFromDays(snapshot.daysUntilDue, t) || translatedFallbackMeta,
      };
    case 'cash_drawer_unreconciled':
      return {
        title: t('alerts.types.cash_drawer_unreconciled.title', { defaultValue: fallbackTitle }),
        subtitle: translateBackendAlertPhrase(
          fallbackSubtitle.replace(/collected today\s*[-–]\s*still open\s*$/i, () =>
            t('alerts.cashDrawer.collectedToday', { defaultValue: 'collected today - still open' })),
          t,
        ),
        meta: translatedFallbackMeta,
      };
    default:
      return { title: fallbackTitle, subtitle: translatedFallbackSubtitle, meta: translatedFallbackMeta };
  }
};

const SNOOZE_OPTIONS: Array<{ key: string; labelKey: string; defaultLabel: string; ms: number }> = [
  { key: '1h', labelKey: 'dashboard.snooze.oneHour', defaultLabel: 'Snooze 1 hour', ms: 60 * 60 * 1000 },
  { key: '4h', labelKey: 'dashboard.snooze.fourHours', defaultLabel: 'Snooze 4 hours', ms: 4 * 60 * 60 * 1000 },
  { key: 'tomorrow', labelKey: 'dashboard.snooze.tomorrow', defaultLabel: 'Until tomorrow', ms: 24 * 60 * 60 * 1000 },
  { key: 'week', labelKey: 'dashboard.snooze.oneWeek', defaultLabel: 'Snooze 1 week', ms: 7 * 24 * 60 * 60 * 1000 },
];

// ---- grouping -------------------------------------------------------------

type GroupedEntry =
  | { kind: 'solo'; alert: DashboardAlert }
  | { kind: 'group'; groupKey: string; members: DashboardAlert[] };

// Walk alerts once. Alerts with a groupKey accumulate under that key in the
// order they first appear; alerts without one are emitted standalone, so the
// dashboard sort order (priority -> severity -> recency) is preserved.
function buildEntries(alerts: DashboardAlert[], enableGrouping: boolean): GroupedEntry[] {
  if (!enableGrouping) {
    return alerts.map((alert) => ({ kind: 'solo' as const, alert }));
  }

  const groupMap = new Map<string, DashboardAlert[]>();
  const order: GroupedEntry[] = [];
  alerts.forEach((alert) => {
    if (!alert.groupKey) {
      order.push({ kind: 'solo', alert });
      return;
    }
    const existing = groupMap.get(alert.groupKey);
    if (existing) {
      existing.push(alert);
      return;
    }
    const members = [alert];
    groupMap.set(alert.groupKey, members);
    order.push({ kind: 'group', groupKey: alert.groupKey, members });
  });
  return order;
}

// ---- row rendering --------------------------------------------------------

interface AlertRowProps {
  alert: DashboardAlert;
  isDismissing: boolean;
  indented?: boolean;
  onClick: (alert: DashboardAlert) => void;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, alert: DashboardAlert) => void;
  optionsLabel: string;
  onSettleAlert?: (alert: DashboardAlert) => void;
  isSettling?: boolean;
}

const AlertRow: React.FC<AlertRowProps> = ({ alert, isDismissing, indented, onClick, onOpenMenu, optionsLabel, onSettleAlert, isSettling }) => {
  const { t } = useTranslation();
  const tone = alertTone[alert.severity];
  // Vehicle recurring bills (insurance / vignette / lease) can be settled inline:
  // one click marks this cycle paid and advances the schedule.
  const canSettle = Boolean(onSettleAlert) && alert.type === 'monthly_fixed_costs_due';
  const iconNode = ALERT_ICON_MAP[alert.iconKey] || <InfoIcon sx={{ fontSize: 18 }} />;
  const priorityDot = PRIORITY_DOT[alert.priority] || null;
  const lastEvent = lastSignificantEvent(alert);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const alertText = getAlertText(alert, t);

  // Audit caption: a one-liner under the subtitle summarising the last
  // operator action. Shown only when there's a meaningful event (skip the
  // implicit 'created' that fires on first reconcile).
  const auditCaption = (() => {
    if (!lastEvent || lastEvent.type === 'created') return null;
    const when = formatRelative(lastEvent.at, t);
    const who = lastEvent.byName || 'system';
    const verbKey = {
      dismissed: 'dashboard.alertsAuditDismissed',
      undismissed: 'dashboard.alertsAuditUndismissed',
      snoozed: 'dashboard.alertsAuditSnoozed',
      unsnoozed: 'dashboard.alertsAuditUnsnoozed',
      assigned: 'dashboard.alertsAuditAssigned',
      unassigned: 'dashboard.alertsAuditUnassigned',
      updated: 'dashboard.alertsAuditUpdated',
    }[lastEvent.type];
    if (!verbKey) return null;
    return t(verbKey, { who, when, defaultValue: `${lastEvent.type} by ${who} - ${when}` });
  })();

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(alert)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(alert);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.1,
        p: 1.15,
        ml: indented ? 3 : 0,
        borderRadius: 1.5,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        cursor: 'pointer',
        outline: 'none',
        transition: 'border-color 0.15s ease, transform 0.15s ease, opacity 0.15s ease',
        opacity: isDismissing ? 0.5 : 1,
        '&:hover': { borderColor: tone.color, transform: 'translateY(-1px)' },
        '&:focus-visible': { boxShadow: `0 0 0 2px ${tone.border}` },
      }}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.3,
            bgcolor: '#FFFFFF',
            color: tone.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {iconNode}
        </Box>
        {priorityDot && (
          <Box
            aria-label={`Priority ${alert.priority}`}
            sx={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: 9,
              height: 9,
              borderRadius: '50%',
              bgcolor: priorityDot,
              border: '2px solid #FFFFFF',
            }}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} color="#111827" noWrap sx={{ minWidth: 0 }}>
            {alertText.title}
          </Typography>
          {alert.assignedTo?.userId && (
            <Tooltip title={alert.assignedTo.name || ''}>
              <Avatar
                sx={{
                  width: 18,
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: '#273B7D',
                  color: '#FFFFFF',
                  flexShrink: 0,
                }}
              >
                {(alert.assignedTo.name || '?').slice(0, 1).toUpperCase()}
              </Avatar>
            </Tooltip>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {alertText.subtitle}
        </Typography>
        {auditCaption && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: '#6B7280', fontStyle: 'italic', fontSize: '0.7rem', mt: 0.25 }}
            noWrap
          >
            {auditCaption}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: tone.color, fontWeight: 500, flexShrink: 0 }}>
        {alertText.meta}
      </Typography>
      {canSettle && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={isSettling}
          startIcon={<PaymentsIcon sx={{ fontSize: 14 }} />}
          onClick={(event) => {
            event.stopPropagation();
            onSettleAlert?.(alert);
          }}
          sx={{
            flexShrink: 0,
            ml: 0.25,
            px: 1,
            py: 0.25,
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: '#273B7D',
            '&:hover': { bgcolor: '#1d2d60' },
          }}
        >
          {isSettling
            ? t('alerts.actions.markingPaid', { defaultValue: 'Marking…' })
            : t('alerts.actions.markPaid', { defaultValue: 'Mark paid' })}
        </Button>
      )}
      {(alert.events && alert.events.length > 0) && (
        <Tooltip title={t('dashboard.alertsHistoryTooltip', { defaultValue: 'View history' })}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setHistoryAnchor(event.currentTarget);
            }}
            sx={{ ml: 0.1, color: tone.color, opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <HistoryIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={optionsLabel}>
        <IconButton
          size="small"
          disabled={isDismissing}
          onClick={(event) => onOpenMenu(event, alert)}
          sx={{ ml: 0.25, color: tone.color, opacity: 0.6, '&:hover': { opacity: 1 } }}
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(historyAnchor)}
        anchorEl={historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 1.5, minWidth: 260, maxWidth: 320 } } }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase" sx={{ display: 'block', mb: 1 }}>
          {t('dashboard.alertsHistoryTitle', { defaultValue: 'Activity log' })}
        </Typography>
        <Stack spacing={0.5}>
          {[...(alert.events || [])].reverse().map((event, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" sx={{ flex: 1 }}>
                <strong>{translateAlertEventType(event.type, t)}</strong>
                {event.byName ? ` - ${event.byName}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatRelative(event.at, t)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Popover>
    </Box>
  );
};

// ---- public component -----------------------------------------------------

export interface AlertListProps {
  alerts: DashboardAlert[];
  onAlertClick: (alert: DashboardAlert) => void;
  /** Required when variant === 'active'. Hides the alert from the active list. */
  onDismissAlert?: (alert: DashboardAlert) => void;
  /** Required when variant === 'active'. */
  onSnoozeAlert?: (alert: DashboardAlert, until: Date) => void;
  /** Required when variant === 'dismissed'. Moves the alert back to active. */
  onRestoreAlert?: (alert: DashboardAlert) => void;
  /**
   * Optional. When provided, the active-mode menu shows "Assign to me" /
   * "Unassign". Pass null as the assignee to clear an existing assignment.
   */
  onAssignAlert?: (alert: DashboardAlert, assignee: { userId: string; name: string } | null) => void;
  /** Optional. Enables an inline "Mark paid" button on vehicle recurring-bill alerts. */
  onSettleAlert?: (alert: DashboardAlert) => void;
  /** Id of the alert currently being settled (shows a busy state on its button). */
  settlingAlertId?: string | null;
  /**
   * Optional. When provided, the active-mode menu shows an extra
   * "Assign to..." item that delegates to a parent-rendered picker
   * (so AlertList doesn't have to know about the employees endpoint).
   */
  onOpenAssigneePicker?: (alert: DashboardAlert) => void;
  /** Identifies "me" for the Assign-to-me action. Required to enable assignment. */
  currentUserId?: string | null;
  /** Display name attached to the assignment record. */
  currentUserName?: string | null;
  dismissingAlertId: string | null;
  /** When true, alerts sharing a groupKey collapse into one expandable row. */
  enableGrouping?: boolean;
  /** Cap visible entries (groups counted as one). Omit for no cap. */
  maxItems?: number;
  /** Slot rendered when there are zero alerts (caller decides empty copy). */
  emptyState?: React.ReactNode;
  /** Determines the kebab menu options. Defaults to 'active'. */
  variant?: 'active' | 'dismissed';
}

const AlertList: React.FC<AlertListProps> = ({
  alerts,
  onAlertClick,
  onDismissAlert,
  onSnoozeAlert,
  onRestoreAlert,
  onAssignAlert,
  onOpenAssigneePicker,
  onSettleAlert,
  settlingAlertId,
  currentUserId,
  currentUserName,
  dismissingAlertId,
  enableGrouping = false,
  maxItems,
  emptyState,
  variant = 'active',
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; alert: DashboardAlert } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const entries = useMemo(
    () => buildEntries(alerts, enableGrouping),
    [alerts, enableGrouping],
  );

  const visible = maxItems ? entries.slice(0, maxItems) : entries;

  const openMenu = (event: React.MouseEvent<HTMLElement>, alert: DashboardAlert) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, alert });
  };
  const closeMenu = () => setMenuAnchor(null);

  const toggleGroup = (groupKey: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  if (alerts.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  const optionsLabel = t('dashboard.alertOptions', { defaultValue: 'Options' });

  return (
    <>
      <Stack spacing={1}>
        {visible.map((entry) => {
          if (entry.kind === 'solo') {
            const alert = entry.alert;
            return (
              <AlertRow
                key={alert._id}
                alert={alert}
                isDismissing={dismissingAlertId === alert._id}
                onClick={onAlertClick}
                onOpenMenu={openMenu}
                optionsLabel={optionsLabel}
                onSettleAlert={onSettleAlert}
                isSettling={settlingAlertId === alert._id}
              />
            );
          }

          // Group entry: render a summary row + expandable list of members.
          const { groupKey, members } = entry;
          const expanded = Boolean(expandedGroups[groupKey]);
          // Borrow tone + icon from the highest-severity member so the
          // summary row matches the most urgent thing in the bucket.
          const lead = members[0];
          const tone = alertTone[lead.severity];
          const iconNode = ALERT_ICON_MAP[lead.iconKey] || <InfoIcon sx={{ fontSize: 18 }} />;
          const labelDef = GROUP_LABELS[groupKey];
          const label = labelDef
            ? t(labelDef.labelKey, { count: members.length, defaultValue: `${members.length} ${labelDef.defaultLabel}` })
            : t('alerts.groups.generic', {
              count: members.length,
              defaultValue: `${members.length} similar alerts`,
            });

          return (
            <Box key={groupKey}>
              <Box
                role="button"
                tabIndex={0}
                onClick={(event) => toggleGroup(groupKey, event as React.MouseEvent)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleGroup(groupKey, event as unknown as React.MouseEvent);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.1,
                  p: 1.15,
                  borderRadius: 1.5,
                  border: `1px dashed ${tone.border}`,
                  bgcolor: tone.bg,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: tone.color },
                  '&:focus-visible': { boxShadow: `0 0 0 2px ${tone.border}` },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.3,
                    bgcolor: '#FFFFFF',
                    color: tone.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {iconNode}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} color="#111827" noWrap>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {expanded
                      ? t('alerts.groups.collapseHint', { defaultValue: 'Click to collapse' })
                      : t('alerts.groups.expandHint', { defaultValue: 'Click to expand' })}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: tone.color, fontWeight: 700, flexShrink: 0 }}>
                  {members.length}
                </Typography>
                <IconButton size="small" sx={{ color: tone.color, opacity: 0.6 }}>
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={expanded} unmountOnExit>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {members.map((member) => (
                    <AlertRow
                      key={member._id}
                      alert={member}
                      indented
                      isDismissing={dismissingAlertId === member._id}
                      onClick={onAlertClick}
                      onOpenMenu={openMenu}
                      optionsLabel={optionsLabel}
                      onSettleAlert={onSettleAlert}
                      isSettling={settlingAlertId === member._id}
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>

      <Menu
        anchorEl={menuAnchor?.el || null}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 200 } }}
      >
        {variant === 'active' ? (() => {
          const alert = menuAnchor?.alert;
          const canAssign = Boolean(onAssignAlert && currentUserId);
          const assignedToMe = Boolean(
            alert?.assignedTo?.userId
            && currentUserId
            && String(alert.assignedTo.userId) === String(currentUserId),
          );

          return [
            ...SNOOZE_OPTIONS.map((option) => (
              <MenuItem
                key={option.key}
                onClick={() => {
                  if (!menuAnchor || !onSnoozeAlert) return;
                  const a = menuAnchor.alert;
                  closeMenu();
                  onSnoozeAlert(a, new Date(Date.now() + option.ms));
                }}
              >
                <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t(option.labelKey, { defaultValue: option.defaultLabel })}</ListItemText>
              </MenuItem>
            )),
            ...((canAssign || onOpenAssigneePicker) ? [
              <Divider key="divider-assign" />,
              ...(canAssign ? [
                assignedToMe ? (
                  <MenuItem
                    key="unassign"
                    onClick={() => {
                      if (!menuAnchor || !onAssignAlert) return;
                      const a = menuAnchor.alert;
                      closeMenu();
                      onAssignAlert(a, null);
                    }}
                  >
                    <ListItemIcon><PersonRemoveIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('dashboard.alertsUnassign', { defaultValue: 'Unassign' })}</ListItemText>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key="assign-me"
                    onClick={() => {
                      if (!menuAnchor || !onAssignAlert || !currentUserId) return;
                      const a = menuAnchor.alert;
                      closeMenu();
                      onAssignAlert(a, { userId: currentUserId, name: currentUserName || 'Me' });
                    }}
                  >
                    <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('dashboard.alertsAssignToMe', { defaultValue: 'Assign to me' })}</ListItemText>
                  </MenuItem>
                ),
              ] : []),
              ...(onOpenAssigneePicker ? [
                <MenuItem
                  key="assign-other"
                  onClick={() => {
                    if (!menuAnchor || !onOpenAssigneePicker) return;
                    const a = menuAnchor.alert;
                    closeMenu();
                    onOpenAssigneePicker(a);
                  }}
                >
                  <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('dashboard.alertsAssignTo', { defaultValue: 'Assign to...' })}</ListItemText>
                </MenuItem>,
              ] : []),
            ] : []),
            <Divider key="divider-dismiss" />,
            <MenuItem
              key="dismiss"
              onClick={() => {
                if (!menuAnchor || !onDismissAlert) return;
                const a = menuAnchor.alert;
                closeMenu();
                onDismissAlert(a);
              }}
              sx={{ color: '#DC2626' }}
            >
              <ListItemIcon sx={{ color: '#DC2626' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('dashboard.dismissAlert', { defaultValue: 'Dismiss' })}</ListItemText>
            </MenuItem>,
          ];
        })() : (
          <MenuItem
            onClick={() => {
              if (!menuAnchor || !onRestoreAlert) return;
              const alert = menuAnchor.alert;
              closeMenu();
              onRestoreAlert(alert);
            }}
            sx={{ color: '#059669' }}
          >
            <ListItemIcon sx={{ color: '#059669' }}><RestoreIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('dashboard.alertsRestore', { defaultValue: 'Restore' })}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default AlertList;
